import { BOSS_OBJECTS, JEFE_2, JEFE_3, JEFE_4, type BossObjectShape, type BossObjectType } from '../../data/boss-objects';
import { GROUND_Y, PLAYER_HITBOX, SIM_HZ, VIEW_WIDTH, WORLD_TOP } from '../../data/tuning';
import { aabbOverlap, circleAabbOverlap, createAabb, createCircle } from '../collision';
import { cropDusterBody, puffCircle, type CropDusterState } from '../enemies/crop-duster';
import { EN02, stepProjectiles, type HelicopterState } from '../enemies/law-helicopter';
import { sombreroCircle, sombreroPosition, type SombreroThrowerState } from '../enemies/sombrero';
import type { PieceState } from './chess';
import type { ChatoState } from './chato';
import { stepWallProjectiles, type WallSearchlightState } from '../enemies/wall-searchlight';
import { createRng, type Rng } from '../rng';
import type { LevelRun } from '../level-runner';
import type { TickContext } from '../tick';

/**
 * Attack objects of a boss fight (GDD §9.2, issue #95): what the patterns of a boss throw at the plane. Coins are
 * collected (+points, never dollars), the rest are damaging projectiles; an object moves in the world with its own
 * velocity (u per tick) and gravity, and the fight cleans them all when it restarts. Pure and deterministic: the
 * PRNG is seeded with the run seed (GDD §15.3).
 */
export interface AttackObject {
  readonly type: BossObjectType;
  readonly shape: BossObjectShape;
  x: number;
  y: number;
  /** Velocity in u per tick and gravity in u per tick squared (positive pulls down). */
  vx: number;
  vy: number;
  gravity: number;
  /** A boomerang horseshoe (JEFE-2, #110): out against the scroll, it turns at a fraction of the view and comes back. */
  boomerang?: { turnFromView: number; speed: number; returning: boolean };
  /** A portrait wall (JEFE-2, #110): a gap whose centre swings around a base and is at the base when the plane passes. */
  wall?: { portrait: string; base: number; passTick: number };
  /** A chess piece of JEFE-3 (#121): its cell, direction and the move it has announced. */
  piece?: PieceState;
  /** A column of the check of JEFE-3 (#121): the centre of its gap; the gap itself closes with the phase (see `checkGap`). */
  check?: { centre: number; gap?: { start: number; end: number; seconds: number }; width?: number };
}

/** A miniboss that the phone of JEFE-3 called (#121): it runs on the rules of its own module (EN-31, EN-32). */
export interface SummonedEnemy {
  readonly type: 'EN-31' | 'EN-32';
  /** Where it entered (the crop duster) or where its balcony stands (the thrower), in the world. */
  readonly startX: number;
  x: number;
  y: number;
  duster: CropDusterState | null;
  thrower: SombreroThrowerState | null;
}

/** Height of the gap of the check columns after `ticks` of the phase: 3.5 u closing linearly to 3.0 u in 10 s (JEFE-3, #121). */
export function checkGap(ticks: number, gap: { start: number; end: number; seconds: number } = { start: JEFE_3.checkGapStart, end: JEFE_3.checkGapEnd, seconds: JEFE_3.checkCloseSeconds }): number {
  const closed = Math.min(1, ticks / (gap.seconds * SIM_HZ));
  return gap.start + (gap.end - gap.start) * closed;
}

/** The two boxes of a check column around its gap at `ticks` of the phase. */
export function checkBoxes(object: AttackObject, ticks: number): { x: number; y: number; width: number; height: number }[] {
  const gap = checkGap(ticks, object.check!.gap);
  const width = object.check!.width ?? JEFE_3.checkWidth;
  const lowTop = object.check!.centre - gap / 2;
  const highBottom = object.check!.centre + gap / 2;
  return [
    { x: object.x, y: (GROUND_Y + lowTop) / 2, width, height: lowTop - GROUND_Y },
    { x: object.x, y: (highBottom + WORLD_TOP) / 2, width, height: WORLD_TOP - highBottom },
  ];
}

/** Height of the centre of the gap of a wall at a tick of the core. */
export function gapCenter(wall: NonNullable<AttackObject['wall']>, tick: number): number {
  return wall.base + JEFE_2.wallAmplitude * Math.sin((2 * Math.PI * JEFE_2.wallHz * (tick - wall.passTick)) / SIM_HZ);
}

/** The two boxes of a wall around its gap at a tick: below it from the ground and above it up to the ceiling. */
export function wallBoxes(object: AttackObject, tick: number): { x: number; y: number; width: number; height: number }[] {
  const centre = gapCenter(object.wall!, tick);
  const lowTop = centre - JEFE_2.wallGap / 2;
  const highBottom = centre + JEFE_2.wallGap / 2;
  return [
    { x: object.x, y: (GROUND_Y + lowTop) / 2, width: JEFE_2.wallWidth, height: lowTop - GROUND_Y },
    { x: object.x, y: (highBottom + WORLD_TOP) / 2, width: JEFE_2.wallWidth, height: WORLD_TOP - highBottom },
  ];
}

/** A launch waiting for its tick (the second hippo of a burst). */
export interface PendingLaunch {
  delay: number;
  speed: number;
}

export interface AttackField {
  objects: AttackObject[];
  rng: Rng;
  readonly seed: number;
  /** The phase the counters below belong to: they restart when it changes. */
  phase: number;
  /** Ticks since the last rain column or burst of the current phase. */
  cadence: number;
  pending: PendingLaunch[];
  /** The law helicopter of phase 3 (EN-02), with its own rules (#84). */
  helicopter: HelicopterState | null;
  /** Every object ever spawned, in order, for the hooks and the checks of determinism. */
  spawnLog: { type: BossObjectType; shape: BossObjectShape; x: number; y: number; portrait?: string; gapBase?: number }[];
  /** Walls spawned so far and the base of the last one (JEFE-2, #110); reset with the fight. */
  walls: number;
  lastBase: number | null;
  /** JEFE-3 (#121): ticks of the current phase (the check closes with them), pieces and check columns spawned, the last centre. */
  phaseTicks: number;
  pieces: number;
  checks: number;
  lastCentre: number | null;
  /** The minibosses the phone has called and the ticks of the rings so far, in order. */
  summoned: SummonedEnemy[];
  rings: number[];
  /** JEFE-4 (#133): where El Chato is (tunnel, warning, out, motorbike) and the wall searchlights of the last phase. */
  chato: ChatoState | null;
  searchlights: WallSearchlightLight[];
}

/** A wall searchlight of El Muro (EN-44 rules, #130), hung from the ceiling at a fraction of the view. */
export interface WallSearchlightLight {
  readonly fromView: number;
  x: number;
  readonly y: number;
  state: WallSearchlightState;
}

export function createAttackField(seed: number): AttackField {
  return { objects: [], rng: createRng(seed), seed, phase: 0, cadence: 0, pending: [], helicopter: null, spawnLog: [], walls: 0, lastBase: null, phaseTicks: 0, pieces: 0, checks: 0, lastCentre: null, summoned: [], rings: [], chato: null, searchlights: [] };
}

/**
 * A phase of JEFE-3 ends (#121): what it left goes with it, the pieces, the check columns and the minibosses the phone
 * called (with their cloud). The other bosses leave none of these.
 */
export function clearPhaseObjects(field: AttackField): void {
  field.objects = field.objects.filter((object) => !object.piece && !object.check);
  field.summoned = [];
  field.rings = [];
  field.objects = field.objects.filter((object) => object.type !== 'barrel');
  field.chato = null;
  field.searchlights = [];
}

/** Back to the start of the fight (a life lost): no objects, no helicopter, the PRNG at its seed. */
export function resetAttacks(field: AttackField): void {
  field.objects = [];
  field.rng = createRng(field.seed);
  field.phase = 0;
  field.cadence = 0;
  field.pending = [];
  field.helicopter = null;
  field.walls = 0;
  field.lastBase = null;
  field.phaseTicks = 0;
  field.pieces = 0;
  field.checks = 0;
  field.lastCentre = null;
  field.summoned = [];
  field.rings = [];
  field.chato = null;
  field.searchlights = [];
}

export interface PatternEnv {
  run: LevelRun;
  attacks: AttackField;
  view: { left: number; right: number };
}

export function spawnObject(
  field: AttackField,
  context: TickContext,
  type: BossObjectType,
  at: { x: number; y: number; vx?: number; vy?: number; gravity?: number; boomerang?: AttackObject['boomerang']; wall?: AttackObject['wall']; piece?: PieceState; check?: AttackObject['check'] },
): AttackObject {
  const object: AttackObject = {
    type,
    shape: BOSS_OBJECTS[type].shape,
    x: at.x,
    y: at.y,
    vx: at.vx ?? 0,
    vy: at.vy ?? 0,
    gravity: at.gravity ?? 0,
    ...(at.boomerang && { boomerang: at.boomerang }),
    ...(at.wall && { wall: at.wall }),
    ...(at.piece && { piece: at.piece }),
    ...(at.check && { check: at.check }),
  };
  field.objects.push(object);
  field.spawnLog.push({ type, shape: object.shape, x: at.x, y: at.y, ...(at.wall && { portrait: at.wall.portrait, gapBase: at.wall.base }) });
  context.emit({ type: 'boss:attack-spawn', objectType: type, shape: object.shape, x: at.x, y: at.y });
  return object;
}

/** Per-tick motion of the objects (before the patterns spawn new ones) and of the helicopter projectiles. */
export function stepAttacks(run: LevelRun, field: AttackField, view: { left: number; right: number }): void {
  const boss = run.boss;
  if (!boss) return;
  if (boss.status === 'retreating' || boss.status === 'done') {
    field.objects = [];
    field.pending = [];
    field.helicopter = null;
    field.summoned = [];
    return;
  }
  for (const object of field.objects) {
    const turning = object.boomerang;
    if (turning) {
      if (!turning.returning && object.x - view.left <= turning.turnFromView * VIEW_WIDTH) turning.returning = true;
      // Out against the scroll, back with it: the speed is relative to the view.
      object.vx = (run.scrollSpeed + (turning.returning ? turning.speed : -turning.speed)) / SIM_HZ;
      if (turning.returning) object.vy = 0;
    }
    object.x += object.vx;
    object.y += object.vy;
    object.vy -= object.gravity;
  }
  // The pieces of JEFE-3 appear a cell beyond the right edge, so they get a wider margin than the projectiles.
  field.objects = field.objects.filter((object) => object.y > -1 && object.x > view.left - 2 && object.x < view.right + (object.piece ? JEFE_3.cellSize + 2 : 2));
  if (field.helicopter) stepProjectiles(field.helicopter, { left: view.left - 2, right: view.right + 2, bottom: -1, top: 14 });
  // The bursts of the wall searchlights of El Muro (JEFE-4, #133) keep flying until they leave the view.
  for (const light of field.searchlights) stepWallProjectiles(light.state, { left: view.left - 2, right: view.right + 2, bottom: -1, top: 14 });
}

/** Phase 4: collecting a coin is worth its points and leaves the dollars alone. */
export function collectAttackPickups(run: LevelRun, field: AttackField, context: TickContext): void {
  const plane = createAabb(run.core.player.x, run.core.player.y, PLAYER_HITBOX.width, PLAYER_HITBOX.height);
  field.objects = field.objects.filter((object) => {
    const def = BOSS_OBJECTS[object.type];
    if (def.kind !== 'pickup') return true;
    if (!circleAabbOverlap(createCircle(object.x, object.y, def.pickupRadius!), plane)) return true;
    run.core.pendingScore += def.points!;
    context.emit({ type: 'boss:coin', objectType: object.type, points: def.points });
    return false;
  });
}

/** Phase 5: a damaging object (or a projectile of the phase-3 helicopter) that touches the plane. */
export function attackCollisions(run: LevelRun, field: AttackField, context: TickContext): void {
  const plane = createAabb(run.core.player.x, run.core.player.y, PLAYER_HITBOX.width, PLAYER_HITBOX.height);
  for (const object of field.objects) {
    const kind = BOSS_OBJECTS[object.type].kind;
    // A chess piece is a column of one cell (JEFE-3, #121): touching it hurts like a column and it stays where it is.
    if (kind === 'piece') {
      const def = BOSS_OBJECTS[object.type];
      if (aabbOverlap(createAabb(object.x, object.y, def.hitbox!.width, def.hitbox!.height), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'column', bossObject: object.type });
      }
      continue;
    }
    // The columns of the check close with the phase (JEFE-3, #121).
    if (kind === 'check') {
      if (checkBoxes(object, field.phaseTicks).some((box) => aabbOverlap(createAabb(box.x, box.y, box.width, box.height), plane))) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'column', bossObject: object.type });
      }
      continue;
    }
    if (kind !== 'wall') continue;
    if (wallBoxes(object, run.core.tick).some((box) => aabbOverlap(createAabb(box.x, box.y, box.width, box.height), plane))) {
      context.emit({ type: 'DAMAGING_COLLISION', source: 'column', bossObject: object.type, portrait: object.wall!.portrait });
    }
  }
  // JEFE-4 (#133): while the motorbike crosses, the lane at ground level (y 1 to y 3) is harmful along the whole view.
  if (field.chato?.state === 'MOTO' && plane.y - plane.halfH < JEFE_4.laneTop) {
    context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', bossObject: 'motorbike-lane' });
  }
  // The bursts of the wall searchlights of El Muro hurt like the projectiles of EN-02 (#130).
  for (const light of field.searchlights) {
    for (const projectile of light.state.projectiles) {
      if (projectile.delay > 0) continue;
      if (circleAabbOverlap(createCircle(projectile.x, projectile.y, EN02.projectileRadius), plane)) {
        context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: 'EN-44', bossObject: 'wall-searchlight' });
      }
    }
  }
  // The minibosses called by the phone of JEFE-3 hurt with their own rules (#121): the plane and its cloud, the sombrero.
  for (const enemy of field.summoned) {
    if (enemy.duster) {
      if (aabbOverlap(cropDusterBody(enemy.x, enemy.y), plane)) context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type });
      for (const puff of enemy.duster.puffs) {
        if (circleAabbOverlap(puffCircle(puff), plane)) context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: enemy.type });
      }
    }
    const at = enemy.thrower ? sombreroPosition(enemy.thrower, enemy) : null;
    if (at && circleAabbOverlap(sombreroCircle(at), plane)) context.emit({ type: 'DAMAGING_COLLISION', source: 'enemy', enemyType: enemy.type });
  }
  field.objects = field.objects.filter((object) => {
    const def = BOSS_OBJECTS[object.type];
    if (def.kind !== 'damaging') return true;
    if (!aabbOverlap(createAabb(object.x, object.y, def.hitbox!.width, def.hitbox!.height), plane)) return true;
    context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', bossObject: object.type });
    return false;
  });
  for (const projectile of field.helicopter?.projectiles ?? []) {
    if (projectile.delay > 0) continue;
    if (circleAabbOverlap(createCircle(projectile.x, projectile.y, EN02.projectileRadius), plane)) {
      context.emit({ type: 'DAMAGING_COLLISION', source: 'projectile', enemyType: 'EN-02', bossObject: 'helicopter' });
    }
  }
}

/** Ticks of a duration in seconds (the cadences of the patterns). */
export const ticksOf = (seconds: number): number => Math.round(seconds * SIM_HZ);

/**
 * Where a decoy that spawns at x must go to be reachable among the gap objects of the fight (§9.1): the centre of the gap of
 * the nearest portrait wall of JEFE-2 or of the nearest check column of JEFE-3, with the x of that object; null if none.
 */
export function gapAnchor(field: AttackField, x: number, tick: number): { x: number; y: number } | null {
  const wall = nearestWall(field, x);
  if (wall) return { x: wall.x, y: gapCenter(wall.wall!, tick) };
  let best: AttackObject | null = null;
  for (const object of field.objects) {
    if (!object.check) continue;
    if (Math.abs(object.x - x) <= JEFE_3.checkSpacing && (!best || Math.abs(object.x - x) < Math.abs(best.x - x))) best = object;
  }
  return best ? { x: best.x, y: best.check!.centre } : null;
}

/** The wall whose gap is nearest to x (within a wall spacing), for the decoys of Los Caballeros to spawn inside it. */
export function nearestWall(field: AttackField, x: number): AttackObject | null {
  let best: AttackObject | null = null;
  for (const object of field.objects) {
    if (!object.wall) continue;
    if (Math.abs(object.x - x) <= JEFE_2.wallSpacing && (!best || Math.abs(object.x - x) < Math.abs(best.x - x))) best = object;
  }
  return best;
}
